import { cva, type VariantProps } from 'class-variance-authority';

import { mergeClasses } from '@solvace/zard/shared/utils/merge-classes';

export const selectVariants = cva(
  mergeClasses(
    'relative inline-block w-full rounded-md group data-active:border data-active:border-ring data-active:ring-ring/50 data-active:ring-[3px]',
    '[&_button]:focus-visible:border [&_button]:focus-visible:border-ring [&_button]:focus-visible:ring-ring/50 [&_button]:focus-visible:ring-[3px]',
  ),
);

export const selectTriggerVariants = cva(
  mergeClasses(
    'flex w-full items-center justify-between gap-2 rounded-md border border-input bg-transparent',
    'shadow-xs transition-[color,box-shadow] outline-none cursor-pointer disabled:cursor-not-allowed',
    'disabled:opacity-50 data-placeholder:text-muted-foreground [&_svg:not([class*="text-"])]:text-muted-foreground',
    'dark:bg-input/30 dark:hover:bg-input/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40',
    'aria-invalid:border-destructive [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*="size-"])]:size-4',
  ),
  {
    variants: {
      zSize: {
        sm: 'min-h-8 py-1 text-xs px-2',
        default: 'min-h-9 py-1.5 px-3 text-sm',
        lg: 'min-h-10 py-2 text-base px-4',
        /** Additive. Solvace design-system select trigger: 44px tall, 14px side padding, 14px text
         *  (design-system/index.html "Sizing & shape" and `.sd`). */
        ds: 'min-h-11 py-2 px-3.5 text-sm',
      },
    },
    defaultVariants: {
      zSize: 'default',
    },
  },
);
/** Solvace design-system droplist panel (`.sd-pop`): white, `#e6e8ee` border, radius 10, `0 12px 30px rgba(13,18,47,.16)` shadow. */
const SELECT_CONTENT_DS_CLASSES = 'rounded-[10px] border-[#e6e8ee] bg-white text-[#0d122f] shadow-[0_12px_30px_rgba(13,18,47,.16)]';

export const selectContentVariants = cva(
  'z-9999 min-w-full overflow-y-auto rounded-md border bg-popover text-popover-foreground shadow-lg animate-in fade-in-0 zoom-in-95',
  {
    variants: {
      /** Additive. `ds` is the design-system panel; `ds-directory` is the same panel grown to the 340px of the prototype
       *  directory droplists (`.sd-pop-dir`: people, area / location, with the search box pinned on top). */
      zAppearance: {
        default: '',
        ds: `${SELECT_CONTENT_DS_CLASSES} max-h-[240px]`,
        'ds-directory': `${SELECT_CONTENT_DS_CLASSES} max-h-[340px]`,
      },
    },
    defaultVariants: {
      zAppearance: 'default',
    },
  },
);
export const selectItemVariants = cva(
  'relative flex min-w-full cursor-pointer text-nowrap items-center gap-2 rounded-sm mb-0.5 outline-hidden select-none hover:bg-accent hover:text-accent-foreground data-selected:bg-accent data-selected:text-accent-foreground data-disabled:pointer-events-none data-disabled:opacity-50 data-disabled:cursor-not-allowed data-disabled:hover:bg-transparent data-disabled:hover:text-current [&_svg:not([class*="text-"])]:text-muted-foreground [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*="size-"])]:size-4',
  {
    variants: {
      zSize: {
        sm: 'min-h-8 py-1 text-xs',
        default: 'min-h-9 py-1.5 text-sm',
        lg: 'min-h-10 py-2 text-base',
        ds: 'min-h-11 py-2 text-sm',
      },
      zMode: {
        normal: 'pr-8 pl-2',
        compact: 'pl-6.5 pr-2',
      },
      /** Additive. `circle-check` is the Solvace design-system droplist row (`.sd-opt.on`): the picked option's text
       *  turns primary (host class bound to `isSelected()`, since the select moves `data-selected` to the focused
       *  row) and an 18px primary circle-check sits 10px from the right edge, with the row keeping the 12px `.sd-opt`
       *  gap before it. */
      zMarker: {
        check: '',
        'circle-check': '',
      },
      /** Additive. `ds` is the Solvace design-system droplist row (`.sd-opt`): padding 8/10 (prototype 9, trimmed so a
       *  20px line fits the 36px virtual row), radius 8, 14px `#33373f`, `#f2f2f4` on hover and on keyboard focus only (`.sd-opt:hover`); the picked row is drawn by
       *  `z-select-item` (primary text, no fill), never through `data-selected`, which the select also moves on open. */
      zAppearance: {
        default: '',
        ds: 'mb-0 rounded-[8px] py-2 pl-2.5 text-sm text-[#33373f] hover:bg-[#f2f2f4] hover:text-[#33373f] focus-visible:bg-[#f2f2f4]',
      },
    },
    compoundVariants: [
      {
        zMode: 'compact',
        zSize: 'sm',
        class: 'pl-5 pr-2',
      },
      {
        zMode: 'normal',
        zMarker: 'circle-check',
        class: 'pr-10',
      },
    ],
    defaultVariants: {
      zMarker: 'check',
      zAppearance: 'default',
    },
  },
);

export const selectItemIconVariants = cva('absolute flex items-center justify-center', {
  variants: {
    // zSize variants are placeholders for compound variant matching
    zSize: {
      sm: '',
      default: '',
      lg: '',
      ds: '',
    },
    zMode: {
      normal: 'right-2',
      compact: 'left-2',
    },
    zMarker: {
      check: 'size-3.5',
      'circle-check': 'size-[18px] text-primary',
    },
  },
  compoundVariants: [
    {
      zMode: 'compact',
      zSize: 'sm',
      class: 'left-1',
    },
    {
      zMode: 'normal',
      zMarker: 'circle-check',
      class: 'right-2.5',
    },
  ],
  defaultVariants: {
    zMarker: 'check',
  },
});

export type ZardSelectSizeVariants = NonNullable<VariantProps<typeof selectTriggerVariants>['zSize']>;
export type ZardSelectAppearanceVariants = NonNullable<VariantProps<typeof selectContentVariants>['zAppearance']>;
export type ZardSelectItemModeVariants = NonNullable<VariantProps<typeof selectItemVariants>['zMode']>;
export type ZardSelectItemMarkerVariants = NonNullable<VariantProps<typeof selectItemVariants>['zMarker']>;
export type ZardSelectItemAppearanceVariants = NonNullable<VariantProps<typeof selectItemVariants>['zAppearance']>;
